import { useRef, useState } from "react";
import type React from "react";
import { cn } from "@/lib/utils";
import type { ValueByVariant } from "@/components/RightStyle/usePropertyStateV2";
import VariantContextMenu, { VariantDot } from "./VariantContextMenu";

export type VariantType =
  | "default"
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl"
  | "hover"
  | "active"
  | "focus"
  | "dark";

type VariantControlProps = {
  valueByVariant: ValueByVariant;
  onVariantChange: (variant: VariantType, value: string | number) => void;
  onClearVariant: (variant: VariantType) => void;
  onClearAllVariants?: () => void;
  children?: (
    variant: VariantType | null,
    expanded: boolean,
    setExpanded: (v: boolean) => void,
  ) => React.ReactNode;
  className?: string;
  tooltipContent?: string;
  currentFlexDirection: string;
  currentJustifyContent: string;
  currentAlignItems: string;
  controlType?:
    | "align"
    | "align-content"
    | "justify-content"
    | "direction-wrap"
    | "text-align"
    | "text-decoration-line"
    | "text-color";
  anchorRef?: React.RefObject<HTMLElement>;
  modalOpen?: boolean;
  setModalOpen?: (open: boolean) => void;
};

export { VariantDot };

export const VariantControl: React.FC<VariantControlProps> = ({
  valueByVariant,
  onVariantChange,
  onClearVariant,
  onClearAllVariants,
  children,
  className,
  currentFlexDirection,
  currentJustifyContent,
  currentAlignItems,
  controlType,
  anchorRef: externalAnchorRef,
  modalOpen: modalOpenProp,
  setModalOpen: setModalOpenProp,
}) => {
  // For backward compatibility with existing usage
  const asModal = Boolean(setModalOpenProp);
  const internalAnchorRef = useRef<HTMLElement>(null);
  const anchorRef = externalAnchorRef || internalAnchorRef;

  // Internal modal state for when no external state is provided
  const [internalModalOpen, setInternalModalOpen] = useState(false);
  const modalOpen = modalOpenProp ?? internalModalOpen;
  const setModalOpen = setModalOpenProp ?? setInternalModalOpen;

  const handleSelectVariant = (variant: VariantType) => {
    // For simple cases without children, we can trigger onVariantChange with a default value
    if (!children && onVariantChange) {
      // Pass a default value - the actual implementation should be in the parent
      onVariantChange(variant, "");
    }
  };

  if (asModal) {
    // Modal usage - render the menu conditionally based on modalOpen state
    return (
      <>
        {modalOpen && (
          <VariantContextMenu
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            anchorRef={anchorRef}
            valueByVariant={valueByVariant}
            onSelectVariant={handleSelectVariant}
            onClearVariant={onClearVariant}
            onClearAllVariants={onClearAllVariants}
            children={children}
            onVariantChange={onVariantChange}
            controlType={controlType}
            currentFlexDirection={currentFlexDirection}
            currentJustifyContent={currentJustifyContent}
            currentAlignItems={currentAlignItems}
          />
        )}
      </>
    );
  }

  // Non-modal usage (context menu style) - render content inline without portal
  // This is used inside ContextMenuContent which handles its own positioning
  return (
    <VariantContextMenu
      isOpen={true} // Always open for context menu style
      onClose={() => {}} // No-op for context menu style
      anchorRef={anchorRef}
      valueByVariant={valueByVariant}
      onSelectVariant={handleSelectVariant}
      onClearVariant={onClearVariant}
      onClearAllVariants={onClearAllVariants}
      children={children}
      onVariantChange={onVariantChange}
      controlType={controlType}
      currentFlexDirection={currentFlexDirection}
      currentJustifyContent={currentJustifyContent}
      currentAlignItems={currentAlignItems}
      usePortal={false} // Don't use portal for context menu - render inline
    />
  );
};
